import React, { useState, useEffect, useRef } from "react";
import "./Charts.scss";

/**
 * BarChart Component
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects with plant_name and amount properties
 * @param {string} props.xAxisLabel - Label for X axis
 * @param {string} props.yAxisLabel - Label for Y axis
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {string} props.currencySymbol - Currency symbol to display
 * @param {Function} props.onBarClick - Function to call when a bar is clicked, receives item data
 * @param {number} props.barWidth - Fixed width for each bar in pixels (default: 50)
 */
const BarChart = ({
  data = [],
  xAxisLabel = "X Axis",
  yAxisLabel = "Y Axis",
  isLoading = false,
  currencySymbol = "₹",
  onBarClick = null,
  barWidth = 50,
  showXAxisLabel = true,
}) => {
  const [sortedData, setSortedData] = useState([]);
  const [maxValue, setMaxValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dataAttempted, setDataAttempted] = useState(false);
  const chartScrollContainerRef = useRef(null);
  const chartContentRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      // Reset animation state when loading starts
      setIsAnimating(false);
    } else {
      // Mark that we've attempted to load data
      setDataAttempted(true);
    }

    // Sort data by plant_name
    if (data && data.length > 0) {
      setIsAnimating(true);

      const processedData = [...data].sort(
        (a, b) =>
          parseInt(a.plant_name || a.project_name) -
          parseInt(b.plant_name || b.project_name)
      );

      setSortedData(processedData);

      // Calculate max value for scaling
      const max = Math.max(...processedData.map((item) => item.amount || 0));
      setMaxValue(max);

      // Reset animation after delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
    } else {
      setSortedData([]);
      setMaxValue(0);
    }
  }, [data, isLoading]);

  // Handle scroll synchronization for x-axis labels when showXAxisLabel is false
  useEffect(() => {
    if (!showXAxisLabel && chartScrollContainerRef.current) {
      const scrollContainer = chartScrollContainerRef.current;
      const labelsContainer = document.querySelector(".below-scroll-labels");

      if (labelsContainer) {
        const handleScroll = () => {
          labelsContainer.scrollLeft = scrollContainer.scrollLeft;
        };

        scrollContainer.addEventListener("scroll", handleScroll);
        return () =>
          scrollContainer.removeEventListener("scroll", handleScroll);
      }
    }
  }, [showXAxisLabel, sortedData]);

  // Handle bar click
  const handleBarClick = (item) => {
    if (onBarClick && typeof onBarClick === "function") {
      onBarClick(item);
    }
  };
  // Function to format indian rupees
  const formatInRupees = (value) => {
    return value.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
    });
  };

  // Function to format numbers in abbreviated form (L for lakhs, Cr for crores)
  const formatAbbreviatedValue = (value) => {
    if (value >= 10000000) {
      // 1 crore or more
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      // 1 lakh or more
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      // 1 thousand or more
      return `₹${(value / 1000).toFixed(1)}K`;
    } else {
      return `₹${value}`;
    }
  };

  // Calculate nice round intervals for y-axis
  const calculateNiceIntervals = (maxVal) => {
    // Find a nice round number above the max value
    let power = Math.floor(Math.log10(maxVal));
    let magnitude = Math.pow(10, power);
    let normalized = maxVal / magnitude; // Between 1 and 10

    let niceMax;
    if (normalized <= 1.2) niceMax = 1.2 * magnitude;
    else if (normalized <= 2.5) niceMax = 2.5 * magnitude;
    else if (normalized <= 5) niceMax = 5 * magnitude;
    else niceMax = 10 * magnitude;

    // Create 5 equal intervals plus the zero point
    const interval = niceMax / 5;
    return Array.from({ length: 6 }, (_, i) => niceMax - i * interval);
  };

  // Calculate y-axis intervals
  const yAxisIntervals = calculateNiceIntervals(maxValue);

  // Calculate total width needed for the chart bars
  const totalChartWidth = sortedData.length * (barWidth + 20); // 20px for margins

  const handleTitle = (item) => {
    const plant = item?.plant_name;
    const project = item?.project_name;

    if (plant && plant.length > 6) {
      return plant.substring(0, 4) + plant.substring(plant.length - 4);
    } else if (project && project.length > 6) {
      return project.substring(0, 4) + project.substring(project.length - 4);
    } else {
      return plant || project || "";
    }
  };
  // console.log(error, "errorerrorerrorerror");

  console.log(data, "hkjaskjfjsaidf");
  return (
    <div className="bar-chart-container">
      {/* Y-axis line */}
      <div className="y-axis-line"></div>

      {/* X-axis line */}
      <div className="x-axis-line"></div>

      {/* Y-axis labels */}
      <div className="y-axis-labels">
        {yAxisIntervals.map((value, i) => (
          <div
            key={i}
            className="y-axis-label"
            style={{
              width: "max-content",
              textAlign: "right",
              transform: i === 5 ? "translateY(1px)" : "none",
            }}
          >
            {formatAbbreviatedValue(Math.round(value))}
          </div>
        ))}
      </div>

      {/* Grid lines */}
      <div className="grid-lines">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`grid-line ${i === 5 ? "hidden" : ""}`}></div>
        ))}
      </div>

      {/* Single scrollable container for both chart bars and labels */}
      <div className="chart-scroll-container" ref={chartScrollContainerRef}>
        <div
          className="chart-content"
          ref={chartContentRef}
          style={{
            width: `${totalChartWidth}px`,
            minWidth: "100%",
            height: "100%",
          }}
        >
          {/* Chart bars */}
          <div className="chart-bars">
            {sortedData.map((item, index) => (
              <div
                key={index}
                className="bar-column"
                style={{
                  width: `${barWidth}px`,
                  marginLeft: "10px",
                  marginRight: "10px",
                  height: "100%",
                  cursor: onBarClick ? "pointer" : "default",
                }}
                onClick={() => handleBarClick(item)}
                title={`${item.plant_name || item.project_name}: ( INV ${
                  item.invoice_count
                }) - ${formatInRupees(item.amount)}`}
              >
                {/* Bar */}
                <div
                  className={`bar ${isAnimating ? "animate" : ""} ${
                    onBarClick ? "clickable" : ""
                  }`}
                  style={{
                    height: `${(item.amount / yAxisIntervals[0]) * 100}%`,
                    width: "100%",
                    maxWidth: "none",
                    position: "absolute",
                    bottom: "0px",
                  }}
                  title={`${item.plant_name || item.project_name}: ( INV ${
                    item.invoice_count
                  }) - ${formatInRupees(item.amount)}`}
                  onClick={() => handleBarClick(item)}
                />

                {/* Value label when showXAxisLabel is false */}
                {!showXAxisLabel && item.amount > 0 && (
                  <div
                    className="value-label"
                    style={{
                      position: "absolute",
                      bottom: "50px",
                      borderLeft: "1px solid #F26738",
                      height: "auto",
                      transform: "rotate(-90deg)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      width: "111px",
                    }}
                  >
                    {"("}
                    <span
                      className="invoice-count"
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      INV&nbsp;
                    </span>
                    <span
                      className="invoice-count"
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      {` ${item.invoice_count}`}
                    </span>
                    {")"}
                    <span style={{ margin: "0 5px " }}>-</span>
                    {formatInRupees(item.amount)}
                  </div>
                )}

                {/* X-axis labels that appear within the scroll container */}
                {showXAxisLabel && (
                  <div className="x-axis-label">
                    {/* {item.plant_name || item.project_name} */}
                    {/* {handleTitle(item)} */}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* X-axis labels that appear below the scroll container when showXAxisLabel is false */}
      {!showXAxisLabel && (
        <div
          className="below-scroll-labels"
          style={{
            position: "absolute",
            left: "68px",
            right: "20px",
            bottom: "7px",
            height: "30px",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            scrollbarWidth: "none", // Hide scrollbar in Firefox
            msOverflowStyle: "none", // Hide scrollbar in IE/Edge
          }}
        >
          <div
            style={{
              width: `${totalChartWidth}px`,
              minWidth: "100%",
              display: "flex",
            }}
          >
            {sortedData.map((item, index) => (
              <div
                key={index}
                style={{
                  width: `${barWidth}px`,
                  marginLeft: "10px",
                  marginRight: "10px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {handleTitle(item)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hide scrollbar for the labels container */}
      {!showXAxisLabel && (
        <style>
          {`
          .below-scroll-labels::-webkit-scrollbar {
            display: none;
          }
          `}
        </style>
      )}

      {/* X-axis title */}
      <div
        className="x-axis-title"
        style={{
          bottom: "-5px",
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          textAlign: "center",
          zIndex: 20,
        }}
      >
        {xAxisLabel}
      </div>
    </div>
  );
};

export default BarChart;
